import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowRight, Play, Gamepad2, Calendar, Monitor, X, ExternalLink, Moon, Sun, FileText } from "lucide-react";
import { GAMES_FALLBACK } from "../mock/mock";
import { Button } from "../components/ui/button";
import HlsPlayer from "../components/HlsPlayer";
import Lightbox from "../components/Lightbox";
import BrandLogo from "../components/BrandLogo";
import { useTheme } from "../context/ThemeContext";
import { trackEvent } from "../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GameDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { dark, toggle } = useTheme();
  const [game, setGame] = useState(() => GAMES_FALLBACK.find((p) => p.id === id) || null);
  const [loading, setLoading] = useState(true);
  const [showTrailer, setShowTrailer] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [allGames, setAllGames] = useState(GAMES_FALLBACK);

  useEffect(() => {
    let active = true;
    axios
      .get(`${API}/games`, { timeout: 25000 })
      .then((res) => {
        if (active && Array.isArray(res.data) && res.data.length) setAllGames(res.data);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    let active = true;
    setLoading(true);
    axios
      .get(`${API}/games/${id}`, { timeout: 25000 })
      .then((res) => {
        if (active && res.data && !res.data.error) setGame(res.data);
      })
      .catch(() => {})
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [id]);

  if (!game && !loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-[#f5fbfc] dark:bg-slate-950">
        <p className="font-display font-700 text-2xl text-slate-900 dark:text-white">Game not found</p>
        <Link to="/" className="text-teal-600 font-semibold">← Back home</Link>
      </div>
    );
  }
  if (!game) return <div className="min-h-screen bg-[#f5fbfc] dark:bg-slate-950" />;

  const heroImg = game.image;
  const shots = game.screenshots && game.screenshots.length ? game.screenshots : [game.image].filter(Boolean);
  const trailer = game.trailer;
  const primaryLink = game.steamLink || game.externalLink || game.officialSite;

  // next/prev game navigation (cycles through all games)
  const idx = allGames.findIndex((g) => g.id === game.id);
  const prevGame = idx > 0 ? allGames[(idx - 1 + allGames.length) % allGames.length] : allGames[allGames.length - 1];
  const nextGame = idx >= 0 ? allGames[(idx + 1) % allGames.length] : null;
  const gotoGame = (g) => g && navigate(`/game/${g.id}`);

  return (
    <div className="min-h-screen bg-[#f5fbfc] dark:bg-slate-950 transition-colors">
      {/* Top bar */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-white/80 dark:bg-slate-900/80 backdrop-blur-lg border-b border-slate-100 dark:border-white/10">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <button onClick={() => navigate("/")} className="flex items-center gap-2.5">
            <BrandLogo variant={dark ? "white" : "dark"} className="w-8 h-8" />
            <span className="font-display font-800 text-base sm:text-lg text-slate-900 dark:text-white">Sharkbowl Games</span>
          </button>
          <div className="flex items-center gap-3">
            <button onClick={toggle} className="w-10 h-10 rounded-full flex items-center justify-center bg-slate-100 dark:bg-white/10 text-slate-700 dark:text-cyan-300 hover:scale-105 transition-transform" aria-label="Toggle theme">
              {dark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            <Link to="/#works" className="flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-white/80 hover:text-teal-600">
              <ArrowLeft className="w-4 h-4" /> <span className="hidden sm:inline">All games</span>
            </Link>
          </div>
        </div>
      </div>

      {/* Hero */}
      <div className="relative pt-16">
        <div className="relative h-[52vh] min-h-[380px] overflow-hidden bg-slate-800">
          {heroImg && <img src={heroImg} alt={game.title} className="w-full h-full object-cover" />}
          <div className="absolute inset-0 bg-gradient-to-t from-[#f5fbfc] dark:from-slate-950 via-slate-900/40 to-slate-900/60" />
          <div className="absolute inset-0 flex items-end">
            <div className="max-w-7xl mx-auto px-6 pb-10 w-full">
              <span className="inline-block text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full mb-4" style={{ background: `${game.accent}22`, color: game.accent }}>
                {game.genre}
              </span>
              <h1 className="font-display font-800 text-4xl sm:text-6xl lg:text-7xl text-white drop-shadow-lg break-words">{game.title}</h1>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12 grid lg:grid-cols-3 gap-12">
        {/* Main */}
        <div className="lg:col-span-2">
          {trailer && (trailer.hls || trailer.youtubeId || trailer.mp4) ? (
            <button onClick={() => setShowTrailer(true)} className="group relative w-full aspect-video rounded-2xl overflow-hidden mb-10 shadow-xl bg-slate-800">
              <img src={trailer.thumbnail || heroImg} alt="trailer" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-slate-900/40 flex items-center justify-center">
                <span className="w-20 h-20 rounded-full bg-white/90 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Play className="w-9 h-9 text-teal-600 ml-1" fill="currentColor" />
                </span>
              </div>
              <span className="absolute bottom-4 left-4 text-white font-semibold bg-black/40 px-3 py-1 rounded-full text-sm">{trailer.name || "Watch trailer"}</span>
            </button>
          ) : (
            heroImg && (
              <div className="w-full aspect-video rounded-2xl overflow-hidden mb-10 shadow-xl bg-slate-800">
                <img src={heroImg} alt={game.title} className="w-full h-full object-cover" />
              </div>
            )
          )}

          <h2 className="font-display font-800 text-2xl text-slate-900 dark:text-white mb-4">About the game</h2>
          <p className="text-lg text-slate-600 dark:text-slate-300 leading-relaxed">{game.description}</p>

          {shots.length > 0 && (
            <>
              <h2 className="font-display font-800 text-2xl text-slate-900 dark:text-white mt-12 mb-5">Screenshots</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {shots.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => setLightboxIndex(i)}
                    className="aspect-video rounded-xl overflow-hidden group bg-slate-200 dark:bg-slate-800 cursor-pointer"
                    aria-label={`View screenshot ${i + 1}`}
                  >
                    <img src={s} alt={`screenshot ${i + 1}`} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Sidebar */}
        <aside className="lg:col-span-1">
          <div className="sticky top-24 rounded-2xl bg-white dark:bg-slate-900 border border-cyan-100 dark:border-white/10 p-6 shadow-[0_10px_40px_rgba(14,127,168,0.08)]">
            {primaryLink && (
              <a href={primaryLink} target="_blank" rel="noopener noreferrer" className="block" onClick={() => trackEvent("play_demo", { game_id: id, label: game.buttonLabel || "View game", destination: primaryLink })}>
                <Button className="w-full h-12 rounded-full bg-teal-600 hover:bg-teal-700 text-white font-semibold mb-3">
                  {game.buttonLabel || "View game"} <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              </a>
            )}
            {game.officialSite && game.steamLink && (
              <a href={game.officialSite} target="_blank" rel="noopener noreferrer" className="block">
                <Button variant="outline" className="w-full h-11 rounded-full border-teal-600 text-teal-700 dark:text-cyan-300 dark:border-cyan-400/40 hover:bg-teal-50 dark:hover:bg-white/5">
                  Official site
                </Button>
              </a>
            )}

            {game.category !== "coming-soon" && (
              <Link to={`/game/${id}/presskit`} className="block mt-3" data-testid="presskit-link">
                <Button variant="outline" className="w-full h-11 rounded-full border-slate-300 dark:border-white/15 text-slate-700 dark:text-white/80 hover:bg-slate-50 dark:hover:bg-white/5 gap-2">
                  <FileText className="w-4 h-4" /> Press Kit
                </Button>
              </Link>
            )}

            <div className="mt-6 space-y-4 text-sm">
              {game.price && (game.price.final || game.price.final === "Free") && (
                <div className="flex items-center justify-between border-b border-slate-100 dark:border-white/5 pb-3">
                  <span className="flex items-center gap-2 text-slate-400 dark:text-slate-500">
                    <Gamepad2 className="w-4 h-4" /> Price
                  </span>
                  <span className="flex items-center gap-2 font-semibold text-slate-800 dark:text-white">
                    {game.price.discount > 0 && (
                      <>
                        <span className="text-xs font-bold px-1.5 py-0.5 rounded bg-green-500/20 text-green-600 dark:text-green-400">-{game.price.discount}%</span>
                        <span className="line-through text-slate-400 text-xs">{game.price.initial}</span>
                      </>
                    )}
                    <span>{game.price.final}</span>
                  </span>
                </div>
              )}
              <Row icon={Gamepad2} label="Genre" value={game.genre} />
              {(game.releaseDate || game.year) && <Row icon={Calendar} label="Released" value={game.releaseDate || game.year} />}
              {game.platforms && <Row icon={Monitor} label="Platforms" value={game.platforms.join(", ")} />}
              {game.hasDemo && <Row icon={Play} label="Demo" value="Available" />}
            </div>
          </div>
        </aside>
      </div>

      {/* Next / Prev game navigation */}
      <div className="max-w-7xl mx-auto px-6 pb-16">
        <div className="grid sm:grid-cols-2 gap-4">
          <button
            onClick={() => gotoGame(prevGame)}
            className="group flex items-center gap-4 rounded-2xl bg-white dark:bg-slate-900 border border-cyan-100 dark:border-white/10 p-4 hover:-translate-y-1 transition-transform text-left shadow-[0_10px_30px_rgba(14,127,168,0.06)]"
          >
            <ArrowLeft className="w-5 h-5 text-teal-600 dark:text-cyan-400 shrink-0" />
            {prevGame && (
              <div className="w-16 h-10 rounded-lg overflow-hidden bg-slate-200 dark:bg-slate-800 shrink-0">
                {prevGame.image && <img src={prevGame.image} alt="" className="w-full h-full object-cover" />}
              </div>
            )}
            <div className="min-w-0">
              <div className="text-xs text-slate-400 font-semibold uppercase tracking-wide">Previous</div>
              <div className="font-display font-700 text-slate-900 dark:text-white truncate">{prevGame ? prevGame.title : ""}</div>
            </div>
          </button>
          <button
            onClick={() => gotoGame(nextGame)}
            className="group flex items-center justify-end gap-4 rounded-2xl bg-white dark:bg-slate-900 border border-cyan-100 dark:border-white/10 p-4 hover:-translate-y-1 transition-transform text-right shadow-[0_10px_30px_rgba(14,127,168,0.06)]"
          >
            <div className="min-w-0">
              <div className="text-xs text-slate-400 font-semibold uppercase tracking-wide">Next game</div>
              <div className="font-display font-700 text-slate-900 dark:text-white truncate">{nextGame ? nextGame.title : ""}</div>
            </div>
            {nextGame && (
              <div className="w-16 h-10 rounded-lg overflow-hidden bg-slate-200 dark:bg-slate-800 shrink-0">
                {nextGame.image && <img src={nextGame.image} alt="" className="w-full h-full object-cover" />}
              </div>
            )}
            <ArrowRight className="w-5 h-5 text-teal-600 dark:text-cyan-400 shrink-0" />
          </button>
        </div>
      </div>

      {/* Trailer modal */}
      {showTrailer && trailer && (trailer.hls || trailer.youtubeId || trailer.mp4) && (
        <div className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowTrailer(false)}>
          <button className="absolute top-6 right-6 text-white/80 hover:text-white" onClick={() => setShowTrailer(false)} aria-label="Close">
            <X className="w-8 h-8" />
          </button>
          <div className="w-full max-w-4xl aspect-video rounded-2xl overflow-hidden shadow-2xl bg-black" onClick={(e) => e.stopPropagation()}>
            {trailer.hls ? (
              <HlsPlayer src={trailer.hls} poster={trailer.thumbnail} className="w-full h-full" />
            ) : trailer.mp4 ? (
              <video
                src={`${process.env.REACT_APP_BACKEND_URL}${trailer.mp4}`}
                poster={trailer.thumbnail ? `${process.env.REACT_APP_BACKEND_URL}${trailer.thumbnail}` : undefined}
                controls
                autoPlay
                playsInline
                className="w-full h-full"
                data-testid="mp4-trailer-video"
              />
            ) : (
              <iframe
                className="w-full h-full"
                src={`https://www.youtube-nocookie.com/embed/${trailer.youtubeId}?autoplay=1&rel=0`}
                title={trailer.name || "Trailer"}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                data-testid="youtube-trailer-iframe"
              />
            )}
          </div>
        </div>
      )}
      {/* Screenshot lightbox */}
      {lightboxIndex !== null && (
        <Lightbox
          images={shots}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onNavigate={(n) => setLightboxIndex(n)}
        />
      )}
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center justify-between border-b border-slate-100 dark:border-white/5 pb-3">
      <span className="flex items-center gap-2 text-slate-400 dark:text-slate-500">
        <Icon className="w-4 h-4" /> {label}
      </span>
      <span className="font-semibold text-slate-800 dark:text-white text-right">{value}</span>
    </div>
  );
}
